import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';
import { filter } from 'rxjs/operators';
import { onUserLogout } from 'src/app/Services/datadog-session';

import { Company, LoginModel, Stations } from './Models/ApplicationModels';
import { BreadcrumbService } from './Services/breadcrumb.service';
import { CacheBusterService } from './Services/cache-buster.service';
import { CommonServiceService } from './Services/common-service.service';
import { CustomToastsService } from './Services/custom-toasts.service';
import { GoogleTranslateService } from './Services/google-translate.service';
import { MasterDataSeriveService } from './Services/master-data-serive.service';
import { SessionTimeoutService } from './Services/session-timeout.service';
import { UserserviceService } from './Services/userservice.service';
import { environment } from './environments/environment';
import { TranslationCommunicationServiceService } from './translation-communication-service.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  providers: []
})
export class AppComponent {
  title = 'app';
  successMsg: string = '';
  showSuccess: boolean = false;
  errorMsg: string = '';
  processMsg: string = '';
  warningMsg: string = '';
  showError: boolean = false;
  showProcessing: boolean = false;
  showWarning: boolean = false;
  qzPermissionMsg: string = '';
  showQzPermissionModal: boolean = false;
  userInfo: LoginModel = new LoginModel();
  companyApiBaseUrl: string = environment.companyApiBaseUrl;

  constructor(
    private cookie: CookieService,
    private commonService: CommonServiceService,
    private userService: UserserviceService,
    private toast: CustomToastsService,
    private router: Router,
    private masterservice: MasterDataSeriveService,
    private breadcrumbService: BreadcrumbService,
    private cacheService: CacheBusterService,
    private sessionTimeoutService: SessionTimeoutService,
    private translationCommunicationService: TranslationCommunicationServiceService,
    private gtService: GoogleTranslateService
  ) { }

  ngOnInit() {
    // Only initialize session timeout tracking for the production module.
    // The SessionTimeoutService will handle this properly on NavigationEnd,
    // but we avoid setting a stale lastActivityTime for admin routes here.
    const isAdmin = window.location.pathname.startsWith('/admin');
    if (!isAdmin) {
      localStorage.setItem('lastActivityTime', Date.now().toString());
    }
    this.sessionTimeoutService.isLoggedOut = false;
    // Don't call startSessionTimer() here — the service's NavigationEnd
    // subscription will handle it once routing resolves.
    this.gtService.init();

    this.cacheService.init();
    this.toast.successMessage$.subscribe(msg => {
      if (msg) {
        this.successMsg = msg;
        this.showSuccess = true;
        this.showProcessing = false; // Immediately hide processing
      } else {
        this.showSuccess = false;
      }
    });

    this.toast.errorMessage$.subscribe(msg => {
      if (msg) {
        this.errorMsg = msg;
        this.showError = true;
        this.showProcessing = false; // Immediately hide processing
      } else {
        this.showError = false;
      }
    });

    this.toast.processMessage$.subscribe(msg => {
      if (msg) {
        this.processMsg = msg;
        this.showProcessing = true;
      } else {
        this.showProcessing = false;
      }
    });

    this.toast.warningMessage$.subscribe(msg => {
      if (msg) {
        this.warningMsg = msg;
        this.showWarning = true;
      } else {
        this.showWarning = false;
      }
    });

    this.toast.qzPermissionModal$.subscribe(msg => {
      if (msg) {
        this.qzPermissionMsg = msg;
        this.showQzPermissionModal = true;
      } else {
        this.showQzPermissionModal = false;
      }
    });

    setInterval(() => {
      const cookies = document.cookie.split(';').map(c => c.trim());
      const logoutCookie = cookies.find(c => c.startsWith('logoutEvent='));

      if (logoutCookie) {
        this.logout(); // Call your logout logic

        // Clear the logoutEvent cookie to prevent repeated logout
        document.cookie = 'logoutEvent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }, 3000); // every 3 seconds

    const perfEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (perfEntries.length && perfEntries[0].type === 'reload') {
      this.onBrowserRefresh();
    }
  }

  onBrowserRefresh() {
    this.userService.userPermissions = null;
    this.userService.rolesLoaded = false;
    this.commonService.storesLoaded = false;
    this.commonService.stores = [];
  }

  toastClose() {
    this.showSuccess = false;
    this.showError = false;
    this.showProcessing = false;
    this.showWarning = false;
  }

  closeQzModal() {
    this.toast.hideQzPermissionModal();
  }

  reloadPage() {
    window.location.reload();
  }

  logout() {
    const isBypassLicense = sessionStorage.getItem('isBypassLicense') === 'true';

    // Set logout cookie for shared domain
    const performLogout = () => {
      const domain = environment.domainName;
      this.cookie.deleteAll('/', domain);
      localStorage.clear();
      sessionStorage.clear();
      document.cookie = `logoutEvent=1; domain=${domain}; path=/;max-age=10`;
      window.location.href = `${environment.companyWebBaseUrl}/shared/login`;
      onUserLogout();
    };

    if (isBypassLicense) {
      performLogout();
    } else {
      // Wait for the release call so it carries the auth token (Interceptor
      // attaches it via HttpClient) instead of racing the navigation below
      // with an unauthenticated fire-and-forget request.
      this.commonService.unassignStationLicenseAndContinue(performLogout, performLogout);
    }
  }
}
